import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from "@angular/core";
import { NgClassType, NgStyleType } from "../../../types";
import { KeyNames } from "../../../constants/key-names";

export type MedModalSkinType = "white" | "blue";

@Component({
  selector: "med-modal",
  templateUrl: "./med-modal.component.html",
  styleUrls: ["./med-modal.component.scss"],
})
export class MedModalComponent implements AfterViewInit, OnDestroy {
  constructor(private renderer: Renderer2,
              private el: ElementRef) {
  }

  private _hideTimeout?: any;
  private _documentEscapeListener?: VoidFunction | null;

  @Input() closeOnEscape = true;
  @Input() showCloseBtn = true;
  @Input() showBackdrop = true;
  @Input() closeableOutside = true;
  @Input() showDuration = 400;
  @Input() hideDuration = 400;
  @Input() maxWidth = "calc(100vw - 108px)";
  @Input() skin: MedModalSkinType = "white";

  @Output() visibleChange = new EventEmitter<boolean>();

  isVisible = false;
  showing = false;
  closing = false;

  get contentStyleList(): NgStyleType {
    return { maxWidth: this.maxWidth };
  }

  @HostBinding("class") get class(): NgClassType {
    return {
      "med-modal": true,
      [this.skin]: true,
      show: this.showing,
      closing: this.closing,
    };
  }

  @HostBinding("style") get style(): NgStyleType {
    return {
      "--show-duration": `${this.showDuration}ms`,
      "--hide-duration": `${this.hideDuration}ms`,
    };
  }

  @Input() set visible(value: boolean) {
    if (value) {
      clearTimeout(this._hideTimeout);

      this.closing = false;
      this.isVisible = true;

      setTimeout(() => {
        this.showing = true;
      }, 0);
    } else {
      this.onClose();
    }
  }

  ngAfterViewInit() {
    if (this.closeOnEscape) {
      this._subscribeDocumentEscape();
    }
  }

  ngOnDestroy() {
    this._unsubscribeDocumentEscape();
  }

  onClose() {
    if (!this.showing) {
      return;
    }

    this.visibleChange.emit(false);
    this.closing = true;
    this.showing = false;

    this._hideTimeout = setTimeout(() => {
      this.closing = false;
      this.isVisible = false;
    }, this.hideDuration);
  }

  onClickOutside(target: EventTarget) {
    if (!this.closeableOutside || (target as Element).closest("[class*=\"picker\"]")) {
      return;
    }

    this.onClose();
  }

  private _subscribeDocumentEscape() {
    const doc = this.el.nativeElement.ownerDocument;

    this._documentEscapeListener = this.renderer.listen(doc, "keyup", event => {
      if (this.isVisible && event.key === KeyNames.ESC) {
        this.onClose();
      }
    });
  }

  private _unsubscribeDocumentEscape() {
    if (!this._documentEscapeListener) {
      return;
    }

    this._documentEscapeListener();
    this._documentEscapeListener = null;
  }
}
